import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const STATUS_BADGE = {
    envoye: { l: "En attente de signature", c: "border-[#B89C6A] text-[#B89C6A]" },
    signe: { l: "Signé ✓", c: "border-green-600 text-green-700" },
    refuse: { l: "Refusé", c: "border-red-600 text-red-700" },
};

export const ProposalsSection = () => {
    const [proposals, setProposals] = useState([]);

    const load = () =>
        axios.get(`${API}/me/proposals`).then((r) => setProposals(r.data.proposals || [])).catch(() => {});

    useEffect(() => { load(); }, []);

    useEffect(() => {
        const sid = new URLSearchParams(window.location.search).get("deposit_session");
        if (!sid) return;
        window.history.replaceState({}, "", "/mon-espace");
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await axios.get(`${API}/payments/status/${sid}`);
                if (data.payment_status === "paid") {
                    toast.success("Acompte reçu ✓ — merci ! Nous planifions le démarrage des travaux.");
                    load();
                    return;
                }
            } catch { /* retry */ }
            if (attempts < 8) setTimeout(poll, 2500);
            else toast.info("Paiement en cours de confirmation — actualisez la page dans un instant.");
        };
        poll();
    }, []);

    if (proposals.length === 0) return null;

    return (
        <section data-testid="my-proposals" className="mb-12">
            <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-[#B89C6A]" />
                Devis à signer
            </h2>
            <div className="space-y-4">
                {proposals.map((p) => (
                    <ProposalCard key={p.id} proposal={p} onChanged={load} />
                ))}
            </div>
        </section>
    );
};

function ProposalCard({ proposal: p, onChanged }) {
    const [sigName, setSigName] = useState("");
    const [accepted, setAccepted] = useState(false);
    const [busy, setBusy] = useState(false);
    const badge = STATUS_BADGE[p.status] || STATUS_BADGE.envoye;

    const sign = async () => {
        if (sigName.trim().length < 3) { toast.error("Tapez votre nom complet pour signer"); return; }
        if (!accepted) { toast.error("Cochez la case d'acceptation"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/me/proposals/${p.id}/sign`, { signature_name: sigName.trim() });
            toast.success("Devis signé ✓ — CM RENOV vous recontacte pour planifier les travaux");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur de signature");
        } finally {
            setBusy(false);
        }
    };

    const refuse = async () => {
        const reason = window.prompt("Souhaitez-vous indiquer un motif ? (facultatif)");
        if (reason === null) return;
        setBusy(true);
        try {
            await axios.post(`${API}/me/proposals/${p.id}/refuse`, { reason: reason || null });
            toast.success("Devis refusé — merci pour votre retour");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    const payMilestone = async (key) => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/me/proposals/${p.id}/pay/${key}`, {
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur de paiement");
            setBusy(false);
        }
    };

    const downloadInvoice = async (invoiceId, number) => {
        try {
            const r = await axios.get(`${API}/invoices/${invoiceId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `facture-${number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Erreur de téléchargement de la facture");
        }
    };

    const downloadPdf = async () => {
        try {
            const r = await axios.get(`${API}/me/proposals/${p.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `devis-${p.number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Erreur de téléchargement");
        }
    };

    return (
        <div data-testid={`proposal-card-${p.id}`} className="lux-card p-6">
            <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
                <div>
                    <div className="tracking-luxe text-[#B89C6A] text-[10px]">{p.number} · valable jusqu'au {p.valid_until}</div>
                    <div className="font-serif-lux text-xl mt-1">{p.title}</div>
                </div>
                <span data-testid="proposal-status" className={`text-[10px] tracking-wider uppercase px-3 py-1.5 border ${badge.c}`}>
                    {badge.l}
                </span>
            </div>

            <div className="border hairline mb-4">
                <table className="w-full text-[12px]">
                    <thead>
                        <tr className="text-left bg-[#1C1E1D] text-[#FAF9F6]">
                            <th className="p-2.5 font-normal tracking-wider uppercase text-[9px]">Désignation</th>
                            <th className="p-2.5 font-normal tracking-wider uppercase text-[9px] text-right">Qté</th>
                            <th className="p-2.5 font-normal tracking-wider uppercase text-[9px] text-right">PU HT</th>
                            <th className="p-2.5 font-normal tracking-wider uppercase text-[9px] text-right">Total HT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {p.lines.map((l, i) => (
                            <tr key={i} className="border-t hairline">
                                <td className="p-2.5">{l.label}</td>
                                <td className="p-2.5 text-right">{l.qty}</td>
                                <td className="p-2.5 text-right">{fmt(l.unit_price)} €</td>
                                <td className="p-2.5 text-right">{fmt(l.qty * l.unit_price)} €</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-end">
                <div className="text-right space-y-1 text-[13px]">
                    <div className="text-[#1C1E1D]/60">Total HT : {fmt(p.total_ht)} €</div>
                    <div className="text-[#1C1E1D]/60">TVA {p.tva_pct} % : {fmt(p.tva)} €</div>
                    <div className="font-serif-lux text-2xl">
                        <span className="text-[#B89C6A]">{fmt(p.total_ttc)} € TTC</span>
                    </div>
                </div>
            </div>

            {p.notes && <p className="mt-3 text-[12px] text-[#1C1E1D]/60 italic">« {p.notes} »</p>}

            {p.status === "signe" && p.signature && (
                <p className="mt-4 text-[12px] text-green-700 border-l-2 border-green-600 pl-3">
                    Signé électroniquement par <b>{p.signature.name}</b> le {p.signature.signed_at?.slice(0, 10)}.
                </p>
            )}

            {p.status === "signe" && p.payment_schedule && (
                <div data-testid="payment-schedule" className="mt-5 border hairline">
                    <div className="bg-[#1C1E1D] text-[#FAF9F6] px-4 py-2 tracking-luxe text-[10px] flex justify-between items-center">
                        <span>Échéancier de paiement</span>
                        {p.fully_paid && <span className="text-green-400">Intégralement réglé ✓</span>}
                    </div>
                    {p.payment_schedule.map((m, i) => {
                        const prevPaid = i === 0 || p.payment_schedule[i - 1].paid;
                        return (
                            <div key={m.key} className="flex items-center justify-between gap-3 px-4 py-3 border-t hairline flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-[13px]">
                                        <span className={m.paid ? "text-green-700" : ""}>{m.paid ? "✓ " : ""}{m.label}</span>
                                        <span className="text-[#B89C6A] ml-2">{m.pct} %</span>
                                    </div>
                                    {m.paid && (
                                        <div className="text-[11px] text-[#1C1E1D]/50 mt-0.5">
                                            Payé le {m.paid_at?.slice(0, 10)}
                                            {m.invoice_number && ` · Facture ${m.invoice_number}`}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="font-serif-lux text-lg">{fmt(m.amount_eur)} €</span>
                                    {m.paid && m.invoice_id && (
                                        <button
                                            data-testid={`invoice-download-${m.key}`}
                                            onClick={() => downloadInvoice(m.invoice_id, m.invoice_number)}
                                            className="text-[10px] tracking-wider uppercase underline hover:text-[#B89C6A]"
                                        >
                                            Facture ↓
                                        </button>
                                    )}
                                    {!m.paid && prevPaid && (
                                        <button
                                            data-testid={`milestone-pay-${m.key}`}
                                            onClick={() => payMilestone(m.key)}
                                            disabled={busy}
                                            className="inline-flex items-center px-4 py-2 bg-[#B89C6A] text-[#1C1E1D] text-[10px] font-medium tracking-[0.15em] uppercase hover:bg-[#1C1E1D] hover:text-[#FAF9F6] transition-colors disabled:opacity-50"
                                        >
                                            💳 Payer
                                        </button>
                                    )}
                                    {!m.paid && !prevPaid && (
                                        <span className="text-[10px] tracking-wider uppercase text-[#1C1E1D]/40">Après palier précédent</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-5 pt-4 border-t hairline space-y-3">
                {p.status === "envoye" && (
                    <>
                        <input
                            data-testid="proposal-sign-name"
                            className="lux-input w-full"
                            placeholder="Tapez votre nom complet pour signer (ex: Jean Dupont)"
                            value={sigName}
                            onChange={(e) => setSigName(e.target.value)}
                        />
                        <label className="flex items-start gap-2 text-[12px] text-[#1C1E1D]/70 cursor-pointer">
                            <input
                                data-testid="proposal-accept-checkbox"
                                type="checkbox"
                                className="mt-0.5 accent-[#B89C6A]"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                            />
                            <span>J'accepte ce devis pour un montant de {fmt(p.total_ttc)} € TTC. Ma signature électronique vaut bon pour accord.</span>
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button data-testid="proposal-sign-btn" onClick={sign} disabled={busy}
                                className="btn-champagne flex-1 justify-center disabled:opacity-50">
                                {busy ? "Signature…" : "✍️ Signer le devis"}
                            </button>
                            <button data-testid="proposal-refuse-btn" onClick={refuse} disabled={busy}
                                className="btn-outline justify-center disabled:opacity-50">
                                Refuser
                            </button>
                        </div>
                    </>
                )}
                <button data-testid="proposal-pdf-btn" onClick={downloadPdf} className="text-[11px] tracking-wider uppercase underline hover:text-[#B89C6A]">
                    Télécharger le PDF {p.status === "signe" ? "signé" : ""} ↓
                </button>
            </div>
        </div>
    );
}
